// Words and figures for the OneHot page. Every number names its source: the announcement
// ("Your AI agent is writing essays to tick boxes", Sqwish Labs, 12 October 2026) or the platform's
// docs and recorded examples. A figure that changes, such as a price, lives here only.
// A kettei model's name is always lowercase and marked *name*, except in an eyebrow, whose
// capitals it takes like the words beside it; a literal is marked `code`. rich.tsx sets them.

export const LINKS = {
  console: 'https://console.sqwish.ai',
  playground: 'https://console.sqwish.ai/#playground',
  docs: 'https://docs.sqwish.ai',
  announcement: '/blog/your-ai-agent-is-writing-essays-to-tick-boxes',
  // The repositories the console's model comparison links to (web/model-comparison.mjs).
  weightsCore: 'https://huggingface.co/Sqwish/Kettei-Core',
  weightsDot: 'https://huggingface.co/Sqwish/Kettei-Dot',
};

export const HERO = {
  eyebrow: 'kettei on OneHot',
  title: 'Decisions your agent can act on.',
  line:
    'Ask a question and list the answers you allow. Our *kettei* models give each one a probability in milliseconds, so your agent can tell a sure answer from a guess.',
};

export type Kind = 'binary' | 'single' | 'ordinal';

// kettei-core's recorded answer to the API reference's example, 26 September 2026
// (src/onehot/decide-example.json in the platform's repository).
export const TICKET = "Our billing export has failed for two days. We need it for today's audit.";

export interface Answer {
  kind: Kind;
  label: string;
  title: string;
  question: string;
  outcomes: [string, number][];
  expected?: number;
  use: string;
}

export const ANSWERS: Answer[] = [
  {
    kind: 'binary',
    label: 'binary',
    title: 'Yes or no',
    question: 'Does the ticket state a deadline within one day?',
    outcomes: [['yes', 0.912], ['no', 0.088]],
    use: 'Act when yes is likely enough, and send the rest to a person.',
  },
  {
    kind: 'single',
    label: 'single',
    title: 'One of several',
    question: 'Which team should handle this ticket?',
    outcomes: [['billing', 0.927], ['technical', 0.054], ['sales', 0.019]],
    use: 'A probability for every option you list, and never one you did not.',
  },
  {
    kind: 'ordinal',
    label: 'ordinal',
    title: 'A score',
    question: "How severe is the impact on the customer's work?",
    // Levels 2, 1 and 0, named after the rubric's lines: blocked, impaired, minor.
    outcomes: [['blocked', 0.844], ['impaired', 0.106], ['minor', 0.051]],
    expected: 1.793,
    use: 'A probability for each level, and the expected level between them.',
  },
];

// One support-agent run, from the announcement's Figure 1 (an illustrative trace), with six of
// its eight steps so each has room at full size on one line. The step that writes says how.
export interface Step {
  question: string;
  kind: Kind | 'write';
  answer: string;
  p?: number;
}

export const RUN: Step[] = [
  { question: 'Which intent?', kind: 'single', answer: 'card_arrival', p: 0.91 },
  { question: 'Which passages?', kind: 'single', answer: 'delivery times', p: 0.88 },
  { question: 'Which tool?', kind: 'single', answer: 'get_card_shipment', p: 0.93 },
  { question: 'Write the reply', kind: 'write', answer: 'token by token' },
  { question: 'Is it grounded?', kind: 'binary', answer: 'yes', p: 0.96 },
  { question: 'Send, or hand over?', kind: 'binary', answer: 'send', p: 0.94 },
];

// The announcement's Table 1 (price per million input tokens, fine-tuning) and its open
// models, with their sizes; fat's and chub's sizes are not published. dot is released as
// open weights only, so it has no price and no fine-tuning.
export interface Model {
  name: string;
  id: string;
  sees: string;
  price?: string;
  tuning?: string;
  weights?: { href: string; size: string };
}

export const MODELS: Model[] = [
  { name: 'fat', id: 'kettei-fat', sees: 'Text and images', price: '$0.06', tuning: 'Fine-tuning in early preview' },
  { name: 'chub', id: 'kettei-chub', sees: 'Text and images', price: '$0.04', tuning: 'Fine-tune it on your data' },
  { name: 'core', id: 'kettei-core', sees: 'Text', price: '$0.02', tuning: 'Fine-tune it on your data', weights: { href: LINKS.weightsCore, size: '3.7B' } },
  { name: 'dot', id: 'kettei-dot', sees: 'Text', weights: { href: LINKS.weightsDot, size: '900M' } },
];

// The demo videos, in client/public/assets/onehot: name.mp4 for the light page and
// name-dark.mp4 for the dark, each with its first frame beside it as its poster, name.jpg or
// name-dark.jpg; every clip loops back to that frame. Each is H.264, which every browser plays,
// at 1600 x 1000 so it fills the 16:10 frame on a 2x screen. The server caches these files for
// a year, so a changed video needs a new file name. A demo without a video shows an empty frame
// instead. A demo's colour is its brand colour, which its line under the video takes while it
// is on show.
export interface Clip {
  src: string;
  poster: string;
}

export interface Demo {
  id: string;
  name: string;
  colour: 'amber' | 'mint' | 'pink';
  title: string;
  text: string;
  video?: Record<'light' | 'dark', Clip>;
}

const clips = (name: string): Record<'light' | 'dark', Clip> => ({
  light: { src: `/assets/onehot/${name}.mp4`, poster: `/assets/onehot/${name}.jpg` },
  dark: { src: `/assets/onehot/${name}-dark.mp4`, poster: `/assets/onehot/${name}-dark.jpg` },
});

export const DEMOS: Demo[] = [
  {
    id: 'browser',
    name: 'Browser use',
    colour: 'amber',
    title: 'Decide what to click next.',
    text: 'The agent is asked for black running shoes in size 42. On each screen *kettei fat* gives every element a probability, and 13 decisions later the order is placed.',
    video: clips('browser'),
  },
  {
    id: 'gaming',
    name: 'Gaming',
    colour: 'mint',
    title: 'Pick the next move.',
    text: '*kettei chub* was never trained to play. It looks at each frame and gives every move a probability. Mario takes the top one, all the way to the flag.',
    video: clips('gaming'),
  },
  {
    id: 'moderation',
    name: 'Moderation',
    colour: 'pink',
    title: 'Check every message against your rules.',
    text: 'In a live chat, *kettei chub* answers a yes or no question for each rule. Clear cases are published or hidden, and the close calls go to a person.',
    video: clips('moderation'),
  },
];

// The announcement's tuning section and the docs' guides to prompt tuning, fine-tuning and
// families. A result is the model's accuracy before and after, and the data it was measured on.
export interface Result {
  model: string;
  on?: string;
  from: string;
  to: string;
}

export const STEPS: { title: string; text: string; result?: Result }[] = [
  {
    title: 'Tune the wording',
    text: 'Prompt tuning rewrites the question, not the model.',
    result: { model: 'core', from: '76.5%', to: '90.0%' },
  },
  {
    title: 'Fine-tune the models',
    text: 'Train *chub* or *core* on your own examples, ready within 15 minutes.',
    result: { model: 'chub', on: 'Banking77', from: '83.1%', to: '93.4%' },
  },
  {
    title: 'Keep only what passes',
    text: 'A new version serves only once it passes its gate. Roll back any time.',
  },
];

export const FACTS: { title: string; text: string }[] = [
  { title: 'Pay for what it reads', text: 'Input tokens only.' },
  { title: 'Pin a version', text: 'Call `support-router@3`, and roll back any time.' },
  { title: 'Fits your stack', text: 'Python, TypeScript, OpenAPI and MCP.' },
];

// The API reference's example as code. Each sample shows the request with its outcome
// descriptions shortened, so its shape reads at a glance; Copy gives the full request.
const DESCRIPTIONS = {
  billing: 'Charges, invoices, and financial exports.',
  technical: 'Other application failures.',
  sales: 'New purchases.',
  yes: 'An explicit deadline is today or within the next 24 hours.',
  no: 'The deadline is later, absent, or unclear.',
  rubric: [
    'Minor inconvenience; the task can still be completed.',
    'The task is impaired, but a workable alternative is stated.',
    'An important task is blocked and no workable alternative is stated.',
  ],
};

const QUESTIONS = {
  team: 'Which team should handle this ticket?',
  urgent: 'Does the ticket state a deadline within one day?',
  impact: "How severe is the reported impact on the customer's work?",
};

// The request body, in JSON that is also a Python and TypeScript literal, written out in
// full or shortened: the ticket to its first sentence and each description to "…".
function body(full: boolean) {
  const d = (text: string) => JSON.stringify(full ? text : '…');
  const context = full ? TICKET : `${TICKET.split('. ')[0]}…`;
  const rubric = DESCRIPTIONS.rubric.map(d).join(full ? ',\n                  ' : ', ');
  return [
    '{',
    '  "model": "kettei-core",',
    `  "context": ${JSON.stringify(context)},`,
    '  "decisions": [',
    '    {"id": "team", "kind": "single",',
    `     "question": ${JSON.stringify(QUESTIONS.team)},`,
    `     "outcomes": {"billing": ${d(DESCRIPTIONS.billing)}, "technical": ${d(DESCRIPTIONS.technical)}, "sales": ${d(DESCRIPTIONS.sales)}}},`,
    '    {"id": "urgent", "kind": "binary",',
    `     "question": ${JSON.stringify(QUESTIONS.urgent)},`,
    `     "outcomes": {"yes": ${d(DESCRIPTIONS.yes)}, "no": ${d(DESCRIPTIONS.no)}}},`,
    '    {"id": "impact", "kind": "ordinal",',
    `     "question": ${JSON.stringify(QUESTIONS.impact)},`,
    `     "outcomes": ["0", "1", "2"], "rubric": [${rubric}]}`,
    '  ]',
    '}',
  ].join('\n');
}

const curl = (full: boolean) => `curl https://api.sqwish.ai/v1/decide \\
  -H "Authorization: Bearer $SQWISH_API_KEY" \\
  -H "Content-Type: application/json" \\
  --data-binary @- <<'JSON'
${body(full)}
JSON`;

const python = (full: boolean) => `# pip install sqwishai
from sqwishai import OneHot

request = ${body(full)}

with OneHot() as client:  # reads SQWISH_API_KEY
    result = client.decide(**request)

print(result["decisions"]["team"]["top"])`;

const typescript = (full: boolean) => `// npm install sqwishai
import { OneHot, type DecideInput } from "sqwishai";

const request: DecideInput = ${body(full)};

const client = new OneHot(); // reads SQWISH_API_KEY
const result = await client.decide(request);

console.log(result.decisions.team.top);`;

// Python first: the longest sample sets the tile's height, so the first view fills it. A
// sample's comment marker lets its comments read quieter than the code.
export const CODE: { label: string; comment?: string; shown: string; full: string }[] = [
  { label: 'Python', comment: '#', shown: python(false), full: python(true) },
  { label: 'TypeScript', comment: '//', shown: typescript(false), full: typescript(true) },
  { label: 'cURL', shown: curl(false), full: curl(true) },
];

// The recorded answer: shown as the fields an application reads first, copied in full.
export const RESPONSE = {
  shown: `{
  "model": "kettei-core",
  "decisions": {
    "team":   {"top": "billing", "p_top": 0.927},
    "urgent": {"top": "yes", "p_top": 0.912},
    "impact": {"top": "2", "expected_level": 1.793}
  }
}`,
  full: `{
  "id": "dec_963b7180cbd24e69a09870f8",
  "model": "kettei-core",
  "decisions": {
    "team": {
      "kind": "single",
      "probabilities": {"billing": 0.927, "technical": 0.054, "sales": 0.019},
      "top": "billing",
      "p_top": 0.927
    },
    "urgent": {
      "kind": "binary",
      "probabilities": {"yes": 0.912, "no": 0.088},
      "top": "yes",
      "p_top": 0.912
    },
    "impact": {
      "kind": "ordinal",
      "probabilities": {"0": 0.051, "1": 0.106, "2": 0.844},
      "top": "2",
      "p_top": 0.844,
      "expected_level": 1.793
    }
  }
}`,
};
